import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CompanyFilters {
  status: string | null;
  hasNoAdmin: boolean | null;
  isVatRegistered: boolean | null;
  hasWebsite: boolean | null;
  suspensionTypes: string[];
  createdDateRanges: { from: string | null; to: string | null }[];
  memberCountRanges: { min: number | null; max: number | null }[];
  page: number;
  pageSize: number;
}

export const DEFAULT_COMPANY_FILTERS: CompanyFilters = {
  status: null,
  hasNoAdmin: null,
  isVatRegistered: null,
  hasWebsite: null,
  suspensionTypes: [],
  createdDateRanges: [],
  memberCountRanges: [],
  page: 1,
  pageSize: 50,
};

export interface CompanyRow {
  id: string;
  name: string;
  status: string | null;
  suspension_type: string | null;
  suspension_reason: string | null;
  suspended_at: string | null;
  email: string | null;
  website: string | null;
  is_vat_registered: boolean | null;
  created_at: string;
  member_count: number;
  admin_count: number;
  has_no_admin: boolean;
}

interface QueryResult {
  total: number;
  page: number;
  total_pages: number;
  rows: CompanyRow[];
}

export function useCompanies(filters: CompanyFilters) {
  const query = useQuery({
    queryKey: ["companies", filters],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("query_companies", {
        p_status: filters.status ?? undefined,
        p_has_no_admin: filters.hasNoAdmin ?? undefined,
        p_is_vat_registered: filters.isVatRegistered ?? undefined,
        p_has_website: filters.hasWebsite ?? undefined,
        p_suspension_types: filters.suspensionTypes.length > 0 ? filters.suspensionTypes : undefined,
        p_created_date_ranges: filters.createdDateRanges.length > 0
          ? filters.createdDateRanges.map(r => ({
              from: r.from ? new Date(r.from + 'T00:00:00').toISOString() : null,
              to: r.to ? new Date(r.to + 'T23:59:59.999').toISOString() : null,
            }))
          : null,
        p_member_count_ranges: filters.memberCountRanges.length > 0
          ? filters.memberCountRanges
          : null,
        p_page: filters.page,
        p_page_size: filters.pageSize,
      });
      if (error) throw error;
      return (data as unknown as QueryResult) ?? null;
    },
  });

  const result = query.data ?? null;
  return {
    companies: (result?.rows ?? []) as CompanyRow[],
    total: result?.total ?? 0,
    totalPages: result?.total_pages ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? (query.error as Error).message : null,
    refetch: query.refetch,
  };
}
